import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "./api/axios";
import { getImageUrl } from "./utils/image";
import { useActivityLogger } from "./hooks/useActivity";

export default function ArticleDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { logUserActivity, loading: loggerLoading } = useActivityLogger();

  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // const [isSaved, setIsSaved] = useState(false);

  // Fetch article details
  useEffect(() => {
    const fetchArticleDetails = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await api.get(`/articles/${id}`);
        const articleData = response.data;

        setArticle(articleData);
        // setIsSaved(articleData.is_saved || false);
        if (!loggerLoading) {
          try {
            logUserActivity(
              "navigation",
              "view_article_details",
              "ArticleDetails",
              `User viewed article details: ${articleData.title} (ID: ${id})`
            );
          } catch (err) {
            logUserActivity(
              "error",
              "log_activity_error",
              "ArticleDetails",
              `Failed to log activity for article details: ${err.message}`
            );
          }
        }
      } catch (err) {
        console.error("Failed to fetch article details:", err);
        setError("Gagal memuat detail artikel");
        if (!loggerLoading) {
          try {
            logUserActivity(
              "error",
              "fetch_article_error",
              "ArticleDetails",
              `Failed to fetch article details for ID: ${id}`
            );
          } catch (logErr) {
            console.error("Failed to log error:", logErr);
          }
        }
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchArticleDetails();
    }
  }, [id]);

  // Handle save/unsave article
  const handleToggleSave = async () => {
    try {
      const payload = {
        url: article.url,
        title: article.title,
      };

      const response = await api.post("/articles/save", payload);
      logUserActivity(
        "user",
        "toggle_save_article",
        "ArticleDetails",
        `User ${response.data.is_saved ? "saved" : "unsaved"} article: ${article.title}`
      );
      // setIsSaved(response.data.is_saved);
    } catch (error) {
      console.error("Failed to toggle save status:", error);
      setError("Gagal mengubah status simpan artikel");
      logUserActivity(
        "user",
        "toggle_save_article_error",
        "ArticleDetails",
        `Error toggling save status for article: ${article.title}`
      );
      alert("Gagal mengubah status simpan artikel");
    }
  };

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return "Tanggal tidak diketahui";

    const date = new Date(dateString);
    return date.toLocaleDateString("id-ID", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  // Parse tags from JSON
  const parseTags = (tagData) => {
    if (!tagData) return [];

    try {
      if (typeof tagData === "string") {
        return JSON.parse(tagData);
      }
      if (Array.isArray(tagData)) {
        return tagData;
      }
      return [];
    } catch (error) {
      console.error("Error parsing tags:", error);
      return [];
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white px-4 py-4 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
          <p className="text-gray-600">Memuat detail artikel...</p>
        </div>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="min-h-screen bg-white px-4 py-4">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => {
              logUserActivity(
                "navigation",
                "go_back_from_article_details",
                "ArticleDetails",
                "User clicked back from article details"
              );
                navigate("/articles");
            }}
          >
            <img src="/icon-back.png" alt="Back" className="h-6 w-6" />
          </button>
          <h1 className="text-xl font-semibold">Artikel</h1>
          <div className="h-6 w-6" />
        </div>
        <div className="text-center mt-20">
          <p className="text-red-600 mb-4">
            {error || "Artikel tidak ditemukan"}
          </p>
          <button
            onClick={() => {
              logUserActivity(
                "navigation",
                "go_back_from_article_details",
                "ArticleDetails",
                "User clicked back from article details"
              );
              navigate("/articles");

            }}
            className="bg-blue-500 text-white px-4 py-2 rounded-lg"
          >
            Kembali
          </button>
        </div>
      </div>
    );
  }

  const tags = parseTags(article.tag);

  return (
    <div className="min-h-screen bg-white px-4 py-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <button
          onClick={() => {
            logUserActivity(
              "navigation",
              "go_back_from_article_details",
              "ArticleDetails",
              "User clicked back from article details"
            );
            navigate("/articles");

          }}
        >
          <img src="/icon-back.png" alt="Back" className="h-6 w-6" />
        </button>
        <h1 className="text-xl font-semibold">Artikel</h1>
        <div className="h-6 w-6" />
      </div>

      {/* Title and Meta */}
      <div className="mb-2">
        <h2 className="text-2xl font-bold mb-4 leading-snug">
          {article.title}
        </h2>
        <p className="text-sm text-gray-500">
          {article.author || "Unknown Author"} |{" "}
          {formatDate(article.published_date)}
        </p>
      </div>

      {/* Image */}
      {article.image && (
        <div className="relative mb-2">
          <img
            src={getImageUrl(article.image)}
            alt="Article Visual"
            className="w-full rounded-xl"
            onError={(e) => {
              e.target.src = "/article-details1.png";
            }}
          />
        </div>
      )}

      <div className="flex items-center justify-between mb-4">
        <p className="text-[12px] text-gray-400">
          Sumber: {article.source || "Unknown Source"}
        </p>
        {/* <button onClick={handleToggleSave}>
          <img
            src={isSaved ? "/icon-bookmark-filled.png" : "/icon-bookmark.png"}
            alt="Bookmark"
            className="h-6 w-6 opacity-90"
          />
        </button> */}
      </div>

      {/* Content */}
      <div className="text-sm text-gray-800 leading-relaxed space-y-3 mb-6">
        <div dangerouslySetInnerHTML={{ __html: article.content }} />
      </div>

      {/* Tags */}
      {tags.length > 0 && (
        <div className="mt-6 text-sm flex items-center gap-3">
          <span className="font-medium text-gray-800">Tag:</span>
          <div className="flex gap-2 flex-wrap">
            {tags.map((tag, index) => (
              <span
                key={index}
                className="bg-gray-100 text-blue-500 px-3 py-1 rounded-md text-xs font-medium"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
